<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <blockquote class="key-takeaway">
          <p>
            <strong>Point clé :</strong> En 2026, la visite virtuelle immobilière professionnelle la moins chère est un smartphone plus une app IA comme Travvir — pas de caméra 360°, pas de photographe, souvent moins d’une heure par annonce. Capturez chaque pièce, laissez l’IA assembler et lier les scènes, puis publiez un seul lien pour WhatsApp, votre site et les portails.
          </p>
        </blockquote>

        <section class="intro">
          <p>
            Les acheteurs s’attendent à parcourir un bien avant de réserver une visite. Les annonces avec visites 360° interactives obtiennent plus de vues, moins de déplacements inutiles et des décisions plus rapides. L’ancienne voie — photographe, caméra type Matterport, délais de plusieurs jours — convient encore au luxe. Pour la plupart des agents, c’est trop lent et trop cher pour chaque annonce.
          </p>
          <p>
            Ce guide pilier est le playbook complet 2026 pour créer des visites virtuelles immobilières avec un simple smartphone. Vous apprendrez le workflow pro le moins cher, quelles apps utiliser, comment tourner pour la qualité, combien de scènes par maison, comment relier les pièces, quelles erreurs tuer la crédibilité, comment l’IA aide, et comment publier et scaler sur plusieurs biens.
          </p>
        </section>

        <section>
          <h2>
            Quelle est la façon la moins chère de créer une visite virtuelle 360° professionnelle avec un simple smartphone ?
          </h2>
          <p>
            <strong
              >La voie professionnelle la moins chère est un smartphone moderne, un logiciel de visite IA avec essai gratuit (comme Travvir), et un workflow pièce par pièce reproductible — le coût peut être de 0 € par annonce si vous avez déjà le téléphone.</strong
            >
          </p>
          <p>
            « Professionnel » ne signifie plus « tourné avec une caméra à 3 000 $ ». Cela signifie un bon éclairage, une couverture complète des pièces, une navigation fluide et un lien partageable qui fonctionne sur mobile. Les capteurs des iPhone et Android récents offrent déjà assez de résolution pour les portails et les téléphones des acheteurs. L’IA remplace le logiciel panorama de bureau d’autrefois.
          </p>
          <p>
            Comparez les coûts. Un scan 3D pro coûte souvent 200–500 $+ par bien, plus les délais. Une caméra 360 dédiée ajoute 300–5 000 $ d’avance. Un workflow téléphone utilise le matériel que vous avez déjà. Vos vrais coûts sont le temps (souvent 20–45 minutes pour une maison typique) et éventuellement un mini trépied à 20 $. C’est pourquoi les agents qui visitent chaque bien standardisent la capture smartphone.
          </p>
          <p>
            Pour rester professionnel, traitez le tournage comme une mini production : home staging, lumières allumées, capture au centre de chaque pièce, publication seulement après un parcours complet de la visite. Travvir est conçu pour cette stack — capture guidée, assemblage IA, hébergement et embed — sans payer du matériel utilisé deux fois par an.
          </p>
        </section>

        <section>
          <h2>
            Quelles applications gratuites ou peu coûteuses transforment des photos de téléphone en visite 360° interactive ? (Mettre en avant Travvir).
          </h2>
          <p>
            <strong
              >Travvir est la meilleure option avec essai gratuit pour transformer des photos de téléphone en visite multi-pièces interactive ; Google Street View est gratuit pour Maps/Business ; Kuula convient si vous avez déjà des images 360 mais est plus faible pour la capture téléphone seule.</strong
            >
          </p>
          <p>
            Toutes les « apps 360 » ne créent pas une visite immobilière. Certaines ne sauvent qu’un panorama. Les agents ont besoin de liaisons multi-pièces, d’une URL partageable et d’un code embed. Voici les options principales en 2026 :
          </p>
          <p>
            Si votre objectif est une visite pour WhatsApp, le site de l’agence et les portails, Travvir est le choix pratique. Capturez et publiez depuis le téléphone sans acheter de caméra. Utilisez Street View en complément pour Maps, et Zillow 3D Home seulement pour le badge Zillow.
          </p>
          <p>
            Pour un comparatif plus large, voir notre <a href="/blog/fr/createur-visite-virtuelle-gratuit">créateur de visite virtuelle gratuit</a> et le <a href="/blog/fr/meilleur-logiciel-visite-virtuelle-immobilier">meilleur logiciel de visite virtuelle immobilier</a>.
          </p>
          <ul>
            <li>
              <strong>Travvir</strong> — Capture smartphone guidée par IA, assemblage automatique, visites multi-pièces, lien hébergé et iframe. Essai gratuit pour créer et partager ; plans payants pour branding et analytics. Téléchargez l'app Travvir pour démarrer votre essai gratuit. Idéal pour les agents sur place.
            </li>
            <li>
              <strong>Google Street View</strong> — Photo Spheres gratuites pour Maps et Google Business. Excellent pour le SEO local ; pas une visite privée complète pour annonces ou site.
            </li>
            <li>
              <strong>Kuula (gratuit)</strong> — Éditeur navigateur pour fichiers 360 existants. Visites gratuites limitées et branding. Mieux pour photographes avec caméra 360.
            </li>
            <li>
              <strong>Zillow 3D Home</strong> — Gratuit mais uniquement sur Zillow. Utile pour le badge 3D ; pas d’export vers votre site ou d’autres portails.
            </li>
          </ul>
        </section>

        <section>
          <h2>
            Comment obtenir une visite de haute qualité sans caméra 360° dédiée ?
          </h2>
          <p>
            <strong
              >La haute qualité vient du staging, d’un éclairage uniforme, d’une capture au centre de la pièce, d’une rotation stable et de l’assemblage IA — pas du fait de posséder une caméra 360 dédiée.</strong
            >
          </p>
          <p>
            Les caméras 360 aident pour la capture en un coup et l’optique constante. Elles ne corrigent pas le désordre, les coins sombres ou les pièces manquantes. Les acheteurs jugent la clarté, la luminosité et la compréhension du plan. Une visite téléphone bien tournée bat souvent un scan pro précipité d’une maison sombre et encombrée.
          </p>
          <p>
            Quatre leviers : staging (retirer photos perso, dégager plans de travail, cacher poubelles et objets d’animaux) ; lumière (ouvrir stores, allumer tout, privilégier le jour) ; position (centre de la pièce à hauteur de poitrine) ; traitement (IA qui équilibre l’exposition et réduit les erreurs d’assemblage — le pipeline Travvir est conçu pour ce niveau téléphone).
          </p>
          <p>
            Options peu coûteuses : mini trépied pour intérieurs sombres, nettoyage de l’objectif, recapture d’une pièce irrégulière. Évitez les HDR artificiels. Naturel, lumineux et complet paraît toujours plus pro.
          </p>
        </section>

        <section>
          <h2>
            Quels réglages téléphone, éclairage et conseils de tournage améliorent le résultat final ?
          </h2>
          <p>
            <strong
              >Utilisez la caméra arrière, nettoyez l’objectif, maximisez une lumière uniforme, restez immobile au centre de la pièce et tournez lentement en suivant les guides de l’application.</strong
            >
          </p>
          <p>
            Les réglages comptent moins que la technique. Tournez avec la caméra arrière principale (pas l’ultra grand-angle sauf demande de l’app). HDR activé si les intérieurs sont naturels ; désactivez-le si les couleurs sont fausses. Évitez le zoom numérique. Verrouillez l’exposition si possible près d’une fenêtre claire — ou laissez Travvir équilibrer les images.
          </p>
          <p>
            Checklist lumière avant d’ouvrir l’app : ouvrir stores et rideaux utiles ; allumer plafonniers, lampes et sous-meubles ; remplacer ampoules grillées ; éviter un seul point lumineux dans une pièce noire ; extérieurs en lumière douce plutôt qu’à midi brutal.
          </p>
          <p>
            Conseils pro : téléphone à hauteur poitrine–yeux, vertical selon l’app, rotation avec les pieds (pas les bras), pause à chaque invite, pas de personnes ni d’animaux. Dans salles de bain et placards, reculez assez pour inclure les équipements. Face à un grand miroir, décalez-vous pour limiter votre reflet.
          </p>
        </section>

        <section>
          <h2>
            Combien de photos ou de scènes faut-il pour couvrir efficacement une maison typique ?
          </h2>
          <p>
            <strong
              >Une maison typique de 3 chambres nécessite environ 8 à 14 scènes (une position principale par pièce majeure, plus entrée, couloir et extérieur) — pas des centaines de photos fixes.</strong
            >
          </p>
          <p>
            Pensez en scènes, pas en « photos ». Chaque scène est un point de vue 360° complet. Dans une scène, l’app peut capturer de nombreuses images que l’IA assemble. Vous ne prenez pas 200 photos d’annonce ; vous vous placez une fois dans chaque espace important (deux fois pour un très grand séjour).
          </p>
          <p>
            Studios et petits appartements : souvent 5–8 scènes. Grandes maisons de luxe : 15–25. Plus n’est pas toujours mieux — des scènes dans des coins vides ralentissent le spectateur. Priorisez cuisine, suite parentale, pièces de vie et extérieur. Travvir indique quand une scène a assez d’images.
          </p>
          <ul>
            <li>
              Façade / curb appeal (1)
            </li>
            <li>
              Entrée ou hall (1)
            </li>
            <li>
              Salon / séjour (1–2)
            </li>
            <li>
              Cuisine (1)
            </li>
            <li>
              Salle à manger (1, ou combinée si open space)
            </li>
            <li>
              Chambre principale (1)
            </li>
            <li>
              Chambres secondaires (1 chacune)
            </li>
            <li>
              Salles de bain principale et secondaire (1 chacune)
            </li>
            <li>
              Couloir ou palier si utile au plan (1)
            </li>
            <li>
              Garage, sous-sol, buanderie si vendeurs (selon besoin)
            </li>
            <li>
              Jardin / terrasse (1)
            </li>
          </ul>
        </section>

        <section>
          <h2>
            Comment relier les pièces avec des hotspots pour une navigation fluide et simple ?
          </h2>
          <p>
            <strong
              >Placez les hotspots sur les portes et transitions claires, utilisez des libellés courts, et ne liez que les chemins qu’un acheteur emprunterait vraiment.</strong
            >
          </p>
          <p>
            Les hotspots sont les flèches cliquables entre scènes 360. Un mauvais placement est la raison n°1 pour laquelle une visite « low cost » paraît amateur. Si les flèches flottent au milieu d’un mur ou sautent de la cuisine à la chambre sans couloir, l’acheteur se perd et part.
          </p>
          <p>
            Dans Travvir, capturez les pièces puis ajoutez ou confirmez les liens avant publication. Parcourez le chemin complet sur votre téléphone comme un acheteur. Si un saut est confus, déplacez le hotspot ou renommez le libellé. Une visite fluide donne l’impression de marcher dans la maison — c’est ce qui convertit en demandes de visite.
          </p>
          <ul>
            <li>
              Hotspots sur portes, arches et escaliers — pas sur des murs vides.
            </li>
            <li>
              Libellés simples : « Cuisine », « Chambre principale », « Jardin ».
            </li>
            <li>
              Démarrer dans la pièce publique la plus impressionnante ou l’entrée.
            </li>
            <li>
              Lier dans l’ordre de marche (entrée → salon → cuisine → couloir → chambres).
            </li>
            <li>
              Prévoir un chemin de retour pour éviter les impasses.
            </li>
            <li>
              Éviter les placards minuscules sauf argument de vente stockage.
            </li>
          </ul>
        </section>

        <section>
          <h2>
            Quelles erreurs rendent une visite low cost peu professionnelle, et comment les éviter ?
          </h2>
          <p>
            <strong
              >Les visites peu professionnelles échouent souvent à cause d’un mauvais éclairage, du désordre, de pièces manquantes, d’un flou, de hotspots confus ou de liens cassés — tout évitable avec une courte checklist.</strong
            >
          </p>
          <p>
            Faites un contrôle de deux minutes avant d’envoyer le lien au vendeur : ouvrez sur téléphone, parcourez de l’entrée au jardin, vérifiez chaque hotspot, confirmez le chargement en données mobiles. Cette habitude sépare « low cost » de « prêt pour l’annonce », même à 0 € de matériel.
          </p>
          <ul>
            <li>
              <strong>Pièces sombres ou jaunes</strong> — Allumer toutes les lumières et ouvrir les stores ; recapturer plutôt que « corriger en post ».
            </li>
            <li>
              <strong>Désordre et objets personnels</strong> — Stager avant de tourner ; en 360, l’acheteur regarde partout.
            </li>
            <li>
              <strong>Cuisine ou suite parentale manquante</strong> — Ne jamais sauter les pièces de décision.
            </li>
            <li>
              <strong>Flou de mouvement</strong> — Tourner plus lentement ; trépied en faible lumière.
            </li>
            <li>
              <strong>Photographe dans les miroirs</strong> — Se repositionner ou recapturer.
            </li>
            <li>
              <strong>Sauts de hotspots aléatoires</strong> — Suivre l’ordre de marche et les portes.
            </li>
            <li>
              <strong>Filigranes et embeds cassés</strong> — Choisir un plan adapté aux clients ; tester le lien sur mobile.
            </li>
            <li>
              <strong>Visites obsolètes</strong> — Retourner après un gros staging ou rénovation.
            </li>
          </ul>
        </section>

        <section>
          <h2>
            Quels outils d’IA aident pour l’assemblage auto, le nettoyage ou la navigation type plan ?
          </h2>
          <p>
            <strong
              >Les plateformes de visite IA comme Travvir assemblent automatiquement les images téléphone, équilibrent l’exposition et créent la navigation multi-pièces ; certains écosystèmes offrent des vues type plan, tandis que les éditeurs photo seuls ne corrigent que des images isolées.</strong
            >
          </p>
          <p>
            Pour l’immobilier, l’IA utile est dans l’app de visite — pas un filtre photo séparé. L’assemblage auto aligne les images en scène 360. L’IA de nettoyage réduit les écarts d’exposition entre fenêtres claires et coins sombres. La navigation (ou le lien guidé) transforme des pièces séparées en parcours.
          </p>
          <p>
            L’IA Travvir est conçue pour l’agent : capture guidée, assemblage cloud sans logiciel de bureau, publication en minutes. Ce n’est pas un outil d’image unique qui ne produit pas de visite interactive.
          </p>
          <p>
            La navigation type plan varie. Certains outils affichent un plan simple ; les plans mesh type Matterport demandent souvent du matériel dédié et coûtent plus cher. Pour la plupart des annonces, des hotspots clairs suffisent. Si un client exige une vraie dollhouse 3D, utilisez un scan spécialisé pour ce bien — et Travvir par défaut pour le reste du portefeuille.
          </p>
        </section>

        <section>
          <h2>
            Comment publier la visite sur WhatsApp, mon site ou les portails d’annonces avec un minimum d’effort ?
          </h2>
          <p>
            <strong
              >Publiez une fois pour obtenir une URL partageable et un code embed, puis collez le lien dans WhatsApp et les portails et l’iframe sur votre site — pas d’export séparé par canal.</strong
            >
          </p>
          <p>
            La distribution minimale est le point des visites hébergées en cloud. Après traitement Travvir :
          </p>
          <p>
            Ne reconstruisez pas la visite pour chaque canal. Une visite maître, plusieurs placements. Si un portail bloque les embeds, l’URL seule fonctionne. Pour la visibilité Google Business de votre agence, ajoutez des photos 360 via Street View — <a href="/blog/fr/visite-virtuelle-google-business">guide visite virtuelle Google Business</a>.
          </p>
          <p>
            Testez toujours le lien publié en navigation privée sur téléphone avant de l’envoyer au vendeur.
          </p>
          <ul>
            <li>
              <strong>WhatsApp / SMS / e-mail</strong> — Copier le lien. Les destinataires ouvrent dans le navigateur mobile ; pas d’app requise pour le spectateur.
            </li>
            <li>
              <strong>Site web</strong> — Coller l’iframe dans un bloc HTML (WordPress, Wix, Squarespace). Voir <a href="/blog/fr/integrer-visite-virtuelle-site-web">intégrer une visite virtuelle</a>.
            </li>
            <li>
              <strong>Portails / MLS</strong> — Coller l’URL dans le champ visite virtuelle. Vérifier que le MLS autorise les liens externes.
            </li>
            <li>
              <strong>Réseaux sociaux</strong> — Partager le lien avec une vignette de la meilleure pièce.
            </li>
          </ul>
        </section>

        <section>
          <h2>
            Quel est le meilleur workflow pour créer la visite assez vite pour plusieurs annonces ?
          </h2>
          <p>
            <strong
              >Utilisez une checklist fixe — staging, lumières, ordre de capture, traitement IA, QA hotspots, publication, collage du lien — pour que chaque annonce prenne environ 20–45 minutes et que chaque agent suive les mêmes étapes.</strong
            >
          </p>
          <p>
            La vitesse à l’échelle est un système, pas un talent. Les agents qui visitent chaque bien utilisent toujours le même ordre. Workflow éprouvé :
          </p>
          <p>
            Habitudes de volume : kit « visite » dans la voiture (trépied, chiffon, lumière portable), noms du type « 12-Rue-Exemple-2026-07 », formation de toute l’équipe sur la même checklist. Quand le workflow est répétitif, vous offrez des visites aussi sur les biens d’entrée de gamme — un vrai avantage en 2026.
          </p>
          <p>
            Travvir soutient ce modèle de volume : pas de logistique caméra ni de facture photographe par bien. Capturez avec le téléphone que vous avez, publiez depuis la même app, passez à l’annonce suivante.
          </p>
          <ol>
            <li>
              <strong>Avant l’arrivée (5 min)</strong> — Confirmer l’accès, demander le désencombrement, charger le téléphone, installer Travvir, noter la liste des pièces.
            </li>
            <li>
              <strong>Préparation sur place (5–10 min)</strong> — Lumières, stores, objets perso hors cadre, objectif propre.
            </li>
            <li>
              <strong>Capture (10–25 min)</strong> — Entrée → salon → cuisine → chambres → sdb → extérieur. Une scène par pièce majeure.
            </li>
            <li>
              <strong>Traitement (quelques minutes)</strong> — Laisser l’IA Travvir assembler pendant un dernier tour ou le trajet suivant.
            </li>
            <li>
              <strong>Liens et QA (5 min)</strong> — Confirmer hotspots, parcourir une fois, recapturer si besoin.
            </li>
            <li>
              <strong>Publication et diffusion (5 min)</strong> — Lien vers MLS, site, WhatsApp vendeur et CRM.
            </li>
          </ol>
        </section>

        <section>
          <h2>Conclusion</h2>
          <p>
            Créer des visites virtuelles immobilières au smartphone n’est plus un compromis — c’est le workflow professionnel par défaut pour les agents qui ont besoin de vitesse, de constance et de faible coût sur chaque annonce. Stagez le bien, éclairez bien, capturez chaque pièce clé, laissez l’IA assembler et lier, puis publiez un lien partout où regardent les acheteurs.
          </p>
          <p>
            Commencez avec Travvir sur votre prochaine annonce : téléchargez l’app, suivez la capture guidée et livrez une visite le jour même. Complétez ce guide avec <a href="/blog/fr/creer-visite-virtuelle-immobilier">créer une visite virtuelle immobilière</a> et <a href="/blog/fr/ajouter-visite-virtuelle-annonce">ajouter une visite virtuelle à une annonce</a> pour opérationnaliser le workflow pour toute l’équipe.
          </p>
        </section>

        <section>
          <h2>Continuez la lecture</h2>
          <ul>
            <li>
              <a href="/blog/fr/creer-visite-virtuelle-immobilier">Créer une visite virtuelle immobilière</a>
            </li>
            <li>
              <a href="/blog/fr/visite-virtuelle-immobilier-smartphone">Visite virtuelle immobilière smartphone</a>
            </li>
            <li>
              <a href="/blog/fr/creer-visite-virtuelle-gratuit">Créer une visite virtuelle gratuitement</a>
            </li>
            <li>
              <a href="/blog/fr/meilleur-logiciel-visite-virtuelle-immobilier">Meilleur logiciel visite virtuelle immobilier</a>
            </li>
            <li>
              <a href="/blog/fr/ajouter-visite-virtuelle-annonce">Ajouter une visite à une annonce</a>
            </li>
            <li>
              <a href="/blog/fr/guide-visite-virtuelle-smartphone">Guide visite virtuelle smartphone</a>
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "guide-ultime-visites-virtuelles-immobilieres-smartphone" && p.lang === "fr",
);

useBlogSeo({
  title: "Guide ultime : visites virtuelles immobilières au smartphone [2026]",
  description: "Le guide complet 2026 pour créer des visites virtuelles immobilières professionnelles avec un simple smartphone. Apps, tournage, hotspots, IA et publication.",
  keywords: "visite virtuelle immobilière smartphone, créer visite virtuelle immobilier téléphone, visite 360 immobilier, travvir immobilier",
  slug: "guide-ultime-visites-virtuelles-immobilieres-smartphone",
  image: postData.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "Quelle est la façon la moins chère de créer une visite virtuelle 360° professionnelle avec un simple smartphone ?",
      answer: "La voie professionnelle la moins chère est un smartphone moderne, un logiciel de visite IA avec essai gratuit comme Travvir, et un workflow pièce par pièce — souvent 0 € par annonce si vous avez déjà le téléphone.",
    },
    {
      question: "Quelles applications gratuites ou peu coûteuses transforment des photos de téléphone en visite 360° interactive ?",
      answer: "Travvir est la meilleure option avec essai gratuit pour des visites multi-pièces interactives depuis le téléphone. Google Street View est gratuit pour Maps ; Kuula convient aux fichiers 360 existants.",
    },
    {
      question: "Comment obtenir une visite de haute qualité sans caméra 360° dédiée ?",
      answer: "La haute qualité vient du staging, d’un éclairage uniforme, d’une capture au centre de la pièce, d’une rotation stable et de l’assemblage IA — pas d’une caméra 360 dédiée.",
    },
    {
      question: "Quels réglages téléphone, éclairage et conseils de tournage améliorent le résultat final ?",
      answer: "Utilisez la caméra arrière, nettoyez l’objectif, maximisez une lumière uniforme, restez au centre de la pièce et tournez lentement en suivant les guides de l’app.",
    },
    {
      question: "Combien de photos ou de scènes faut-il pour couvrir efficacement une maison typique ?",
      answer: "Une maison typique de 3 chambres nécessite environ 8 à 14 scènes — une position principale par pièce majeure, plus entrée, couloir et extérieur.",
    },
    {
      question: "Comment relier les pièces avec des hotspots pour une navigation fluide et simple ?",
      answer: "Placez les hotspots sur les portes et transitions claires, utilisez des libellés courts, et ne liez que les chemins qu’un acheteur emprunterait vraiment.",
    },
    {
      question: "Quelles erreurs rendent une visite low cost peu professionnelle, et comment les éviter ?",
      answer: "Mauvais éclairage, désordre, pièces manquantes, flou, hotspots confus ou liens cassés — tout évitable avec une courte checklist avant publication.",
    },
    {
      question: "Quels outils d’IA aident pour l’assemblage auto, le nettoyage ou la navigation type plan ?",
      answer: "Les plateformes comme Travvir assemblent les images téléphone, équilibrent l’exposition et créent la navigation multi-pièces ; les éditeurs photo seuls ne suffisent pas.",
    },
    {
      question: "Comment publier la visite sur WhatsApp, mon site ou les portails d’annonces avec un minimum d’effort ?",
      answer: "Publiez une fois pour obtenir une URL et un embed, puis collez le lien dans WhatsApp et les portails et l’iframe sur votre site.",
    },
    {
      question: "Quel est le meilleur workflow pour créer la visite assez vite pour plusieurs annonces ?",
      answer: "Utilisez une checklist fixe — staging, lumières, capture, IA, QA hotspots, publication — pour environ 20–45 minutes par annonce.",
    }
  ],
});
</script>

<style scoped>
.key-takeaway {
  border-left: 4px solid var(--color-primary, #2563eb);
  margin: 0 0 1.5rem;
  padding: 1rem 1.25rem;
  background: rgba(37, 99, 235, 0.06);
}
.key-takeaway p {
  margin: 0;
}
</style>

